/**
 * Client-side image validation and processing for clinic logos.
 * All images are resized to 256x256 and converted to webp before upload.
 */

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 524288; // 500KB
const TARGET_SIZE = 256;

/**
 * Validate a logo file before processing.
 * Returns { valid: true } or { valid: false, error: string }
 */
export const validateLogoFile = (file) => {
  if (!file) {
    return { valid: false, error: 'Δεν επιλέχθηκε αρχείο.' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Μη αποδεκτός τύπος αρχείου. Επιτρέπονται: PNG, JPG, WebP, SVG.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Το αρχείο υπερβαίνει τα 500KB.' };
  }

  return { valid: true };
};

/**
 * Process a logo image: resize to 256x256 (center-crop to square) and convert to webp.
 * Returns a Promise<Blob> of the processed webp image.
 */
export const processLogoImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width = TARGET_SIZE;
      canvas.height = TARGET_SIZE;
      const ctx = canvas.getContext('2d');

      // Center-crop to square
      const size = Math.min(img.width, img.height);
      const sx = (img.width - size) / 2;
      const sy = (img.height - size) / 2;

      ctx.drawImage(img, sx, sy, size, size, 0, 0, TARGET_SIZE, TARGET_SIZE);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Αποτυχία μετατροπής εικόνας.'));
          }
        },
        'image/webp',
        0.85
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Αποτυχία φόρτωσης εικόνας.'));
    };

    img.src = url;
  });
};
